import { flash, FormGroup, Label, SubmitButton, Textarea } from "@xola/ui-kit";
import { requiredString } from "common";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { BaseSchedule, Delegate, Guide } from "types";
import { object, string } from "yup";
import { api } from "../../api/api";
import { ScrollToFieldError } from "../../helpers/validationHelper";
import { useSeller, useUser } from "../../hooks/useAccount";
import { useInfiniteCollection } from "../../hooks/useInfiniteCollection";
import { useInvalidate } from "../../hooks/useInvalidation";
import { useResource } from "../../hooks/useResource";
import { xolaApiUrl } from "../../utils/url";
import { ExternalCalendarLinkModal } from "../Common/ExternalCalendarLinkModal";
import { ImageUploadInput } from "../Common/ImageUploadInput";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";
import { ContentLayout } from "../Layout/ContentLayout";
import { GuideSchedule } from "../Resources/GuideManagement/GuideSchedule";

export interface GuideProfileData {
    id?: ID;
    seller: { id: ID };
    bio?: string;
    schedules?: BaseSchedule[];
    picture?: { id: ID; src: string } | null;
}

const validationSchema = object().shape({
    seller: object().shape({
        id: requiredString(),
    }),
    bio: string(),
});

const initialValues = (sellerId: ID, guide?: Guide) => {
    const values: GuideProfileData = {
        id: guide?.id,
        seller: { id: sellerId },
        bio: guide?.bio ?? "",
        schedules: [],
        picture: guide?.picture ?? null,
    };

    return values;
};

export const GuideProfile = () => {
    const seller = useSeller();
    const user = useUser();

    const { data: guides } = useInfiniteCollection<Guide>(["sellers", seller.id, "guides"], { limit: 500 });

    const guide = guides.find((guide: Guide) => guide.user.id === user.id);

    const invalidate = useInvalidate();
    const [syncCalendarOpen, setSyncCalendarOpen] = useState(false);
    const { data: delegate } = useResource<Delegate>(["users", seller.id, "delegates", guide?.user.id]);

    const { mutateAsync: saveGuide, isLoading } = useMutation(async (payload: GuideProfileData) => {
        return api.put(`/sellers/${payload.seller.id}/guides/${payload.id}`, payload);
    });

    const { mutateAsync: deleteGuidePhoto } = useMutation(
        async ({ sellerId, guideId, mediaId }: { sellerId: ID; guideId: ID; mediaId: ID }) => {
            await api.delete(`/sellers/${sellerId}/guides/${guideId}/medias/${mediaId}`);
        },
    );

    const handleGuidePhotoDeleted = async (initData: GuideProfileData, initGuide?: Guide) => {
        const isGuidePhotoDeleted = initGuide?.picture?.id && !initData.picture;

        if (isGuidePhotoDeleted && initData?.id && initGuide?.picture) {
            await deleteGuidePhoto({
                sellerId: initData.seller.id,
                guideId: initData.id,
                mediaId: initGuide.picture.id,
            });
        }
    };

    // image url' suffix for reloading the browser cache once image is uploaded
    const [pictureSuffix, setPictureSuffix] = useState(Date.now());

    const data = initialValues(seller.id, guide);

    const handleSubmit = async (data: GuideProfileData) => {
        await handleGuidePhotoDeleted(data, guide);

        await saveGuide(data);

        invalidate("guides", guide?.id);
        invalidate("delegates");

        flash.success("Guide profile details updated");
    };

    const handlePictureUpdated = () => {
        invalidate("guides", guide!.id);
        setPictureSuffix(Date.now());
    };

    const updatePictureUrl = guide ? xolaApiUrl(`/sellers/${seller?.id}/guides/${guide.id}/media`) : undefined;

    const getPictureSource = (pictureId: string | undefined) =>
        pictureId
            ? xolaApiUrl(`/sellers/${seller.id}/guides/${guide?.id}/picture?size=medium&${pictureSuffix}`)
            : undefined;

    return (
        <ContentLayout className="space-y-6">
            <XolaFormik
                enableReinitialize
                validateOnChange
                validateOnBlur={false}
                validationSchema={validationSchema}
                initialValues={data}
                onSubmit={handleSubmit}
            >
                {({ handleSubmit, handleReset, values, setFieldValue }) => (
                    <>
                        {guide?.id && (
                            <fieldset className="flex-1 space-y-12">
                                <div className="space-y-4">
                                    <h4 className="font-bold">Guide Profile</h4>
                                    <FormGroup>
                                        <Label>Bio</Label>
                                        <Textarea
                                            value={values.bio}
                                            size="large"
                                            rows={6}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                setFieldValue("bio", e.target.value);
                                            }}
                                        />
                                    </FormGroup>
                                </div>

                                <div className="space-y-4">
                                    <h4 className="font-bold">Schedule</h4>
                                    <FormGroup>
                                        <GuideSchedule guide={guide} />
                                    </FormGroup>
                                </div>

                                <div className="space-y-4">
                                    <h4 className="font-bold">Photo</h4>
                                    <ImageUploadInput
                                        src={getPictureSource(values?.picture?.id)}
                                        hasDelete={!!values?.picture?.id}
                                        uploadUrl={updatePictureUrl ?? ""}
                                        onDelete={() => setFieldValue("picture", null)}
                                        onUpdated={handlePictureUpdated}
                                        onError={(error: string) => flash.danger(error)}
                                    />
                                </div>

                                <div className="flex items-center justify-between">
                                    <h4 className="font-bold">External calendar link</h4>
                                    <SecondaryButton
                                        size="small"
                                        onClick={() => {
                                            setSyncCalendarOpen(true);
                                        }}
                                    >
                                        Click to copy
                                    </SecondaryButton>
                                </div>
                            </fieldset>
                        )}

                        <ContentLayout.Footer preferenceSidebar>
                            <SubmitButton isLoading={isLoading} type="submit" onClick={handleSubmit}>
                                Save
                            </SubmitButton>

                            <SecondaryButton type="reset" onClick={handleReset}>
                                Cancel
                            </SecondaryButton>
                        </ContentLayout.Footer>

                        <ScrollToFieldError />
                    </>
                )}
            </XolaFormik>

            <ExternalCalendarLinkModal
                isOpen={syncCalendarOpen}
                userId={delegate?.user.id}
                onClose={() => setSyncCalendarOpen(false)}
            />
        </ContentLayout>
    );
};
